'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { SignInPasswordlessConfirmation } from '@/app_components/SignIn/Confirmations/SignInPasswordlessConfirmation';
import { SignInPasswordlessNewAccountConfirmation } from '@/app_components/SignIn/Confirmations/SignInPasswordlessNewAccountConfirmation';
import { SignInCreateAccountForm } from '@/app_components/SignIn/Forms/SignInCreateAccountForm';
import { SignInInitialEmailForm } from '@/app_components/SignIn/Forms/SignInInitialEmailForm';
import { SignInPasswordedForm } from '@/app_components/SignIn/Forms/SignInPasswordedForm';
import { SocialSignIn } from '@/app_components/SignIn/SSO/SocialSignIn';
import { defaultPianoPageConfig } from '@/app_lib/defaults';
import { useUser } from '@/app_lib/providers/UserProvider';
import type { SignInFlow } from '@/app_lib/types/accounts/signInFlow.entities';
import {
  type SignInStatus,
  SOCIAL_LOGIN_STATUSES,
  type SocialUrls,
  type UserConsentLocation,
} from '@/app_lib/types/accounts/signin';
import { getRedirectCookieKey } from '@/app_lib/utils/signInFlowHelpers';
import { setRedirectCookie } from '@/app_lib/utils/signinRedirect';

interface SignInFlowManagerProps {
  flow: SignInFlow;
  socialUrls: SocialUrls;
  consentLocation: UserConsentLocation;
  /** Validated redirect path from server; client saves to cookie and cleans URL */
  signinRedirect?: string | null;
}

export const SignInFlowManager = ({ flow, socialUrls, consentLocation, signinRedirect }: SignInFlowManagerProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { setPianoPageConfig } = useUser();

  const [socialLoginLoading, setSocialLoginLoading] = useState(Boolean(searchParams?.get('social_response_id')));
  const [disableSocialLogins, setDisableSocialLogins] = useState(false);

  const [signInStatus, setSignInStatus] = useState<SignInStatus>('Pending');
  const [email, setEmail] = useState('');

  const redirectCookieKey = getRedirectCookieKey(flow);

  const showSocialLogin = SOCIAL_LOGIN_STATUSES.includes(signInStatus) && Object.keys(socialUrls).length > 0;

  useEffect(() => {
    setPianoPageConfig(defaultPianoPageConfig);
  }, [setPianoPageConfig]);

  useEffect(() => {
    if (!signinRedirect) {
      return;
    }
    setRedirectCookie(redirectCookieKey, signinRedirect);
    const cleanParams = new URLSearchParams();
    searchParams?.forEach((value, key) => {
      if (key !== 'signin_redirect') {
        cleanParams.set(key, value);
      }
    });
    const cleanUrl = cleanParams.toString() ? `${pathname ?? '/signin'}?${cleanParams}` : (pathname ?? '/signin');
    router.replace(cleanUrl);
  }, [signinRedirect, pathname, redirectCookieKey, router, searchParams]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Scroll on mode change
  useEffect(() => {
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }, [signInStatus]);

  const handleEditEmailClick = () => {
    setSignInStatus('Pending');
    setEmail('');
  };

  const renderContent = () => {
    switch (signInStatus) {
      case 'Passworded':
        return (
          <SignInPasswordedForm
            disabled={socialLoginLoading}
            email={email}
            flow={flow}
            onEditEmailClick={handleEditEmailClick}
            redirectCookieKey={redirectCookieKey}
            setDisableSocialLogins={setDisableSocialLogins}
          />
        );
      case 'Passwordless':
        return (
          <SignInPasswordlessConfirmation
            email={email}
            flow={flow}
            redirectCookieKey={redirectCookieKey}
          />
        );
      case 'Creating':
        return (
          <SignInCreateAccountForm
            consentLocation={consentLocation}
            disabled={socialLoginLoading}
            email={email}
            flow={flow}
            onEditEmailClick={handleEditEmailClick}
            setDisableSocialLogins={setDisableSocialLogins}
            setSignInStatus={setSignInStatus}
          />
        );
      case 'PasswordlessNewAccount':
        return (
          <SignInPasswordlessNewAccountConfirmation
            email={email}
            flow={flow}
            redirectCookieKey={redirectCookieKey}
          />
        );
      default:
        return (
          <SignInInitialEmailForm
            disabled={socialLoginLoading}
            flow={flow}
            setDisableSocialLogins={setDisableSocialLogins}
            setEmail={setEmail}
            setSignInStatus={setSignInStatus}
          />
        );
    }
  };

  return (
    <div className='flex flex-col layout-gap-sm items-center justify-center w-full max-w-[340px] font-graphik-compact'>
      {renderContent()}
      {showSocialLogin && (
        <SocialSignIn
          disableSocialLogins={disableSocialLogins}
          flow={flow}
          redirectCookieKey={redirectCookieKey}
          setSocialLoginLoading={setSocialLoginLoading}
          socialLoginLoading={socialLoginLoading}
          socialUrls={socialUrls}
        />
      )}
    </div>
  );
};
